import { Icon, Text, Tooltip, HStack, Stack, HStackTypes } from '@genial/components';
import { Pressable, IPressableProps } from 'native-base';
import React from 'react';

interface TableHeadContainerProps extends HStackTypes.Props {
  children: React.ReactNode;
}

export interface TableHeadItemRecurse<T = any> {
  title: string;
  flex?: number;
  orderValue?: keyof T;
  tooltip?: string;
}

export type TableHeadItemOrderBy = { value: string; direction: 'asc' | 'desc' };

/**
 * Defines how many states a column cycles through on repeated presses.
 * 'partial' alternates asc and desc, 'full' adds a third press that clears the ordering.
 */
export type OrderCycleMode = 'full' | 'partial';

interface TableHeadItemProps extends IPressableProps {
  title: string;
  onPress?: () => void;
  value?: TableHeadItemRecurse['orderValue'];
  orderBy?: TableHeadItemOrderBy;
  tooltip?: string;
}

export const TableHeadItem: React.FC<TableHeadItemProps> = ({
  title,
  onPress,
  value,
  orderBy,
  tooltip,
  flex = 1,
  ...rest
}) => {
  const iconName =
    orderBy?.direction === 'asc' && value === orderBy?.value ? 'ArrowUp' : 'ArrowDown';
  const iconColor = value === orderBy?.value ? 'neutral.darkest' : 'neutral.light';

  return (
    <Pressable p={0} variant="unstyled" onPress={onPress} flex={flex} bg={rest.bg} maxW={rest.maxW}>
      <HStack space="inline.quarck">
        <Text variant="semibold" noOfLines={1} color="neutral.darkest" fontSize="xs">
          {title}
        </Text>
        {!!value && (
          <Icon
            size="xxxxs"
            testID="table-head-item-arrow-icon"
            name={iconName}
            color={iconColor}
          />
        )}
        {!!tooltip && (
          <Tooltip label={tooltip} maxW="236px">
            <Stack>
              <Icon name="Info" size="xxxs" color="brand.primary.principal" />
            </Stack>
          </Tooltip>
        )}
      </HStack>
    </Pressable>
  );
};

export const TableHeadContainer: React.FC<TableHeadContainerProps> = ({ children, ...rest }) => {
  return (
    <HStack
      display={{ base: 'none', lg: 'flex' }}
      justifyContent="space-between"
      bg="brand.tertiary.lightest"
      px="inline.xxs"
      py="inline.nano"
      borderRadius="sm"
      w="full"
      {...rest}>
      {children}
    </HStack>
  );
};
